/**
 * Age and length of service.
 *
 * Two distributions side by side, because they only mean something together: a
 * median age of 33 with a median service of under two years is not a young
 * workforce that has grown up here, it is a workforce that has largely arrived
 * recently. The tiles split median age and median service by contract type for
 * the same reason — the fixed-term cohort is materially younger than the
 * permanent one, and that contrast is the finding, not the blended median.
 *
 * Built on the house BarRow rather than a chart component on purpose: a band
 * count can arrive as null, which means WITHHELD to protect small groups (fewer
 * than five people, or a band that would let such a group be worked out from
 * the total), not zero. BarRow draws no bar for it; a bar chart would have to be
 * talked out of plotting a zero. Nulls are kept out of the max and out of every
 * sum here. A key the response does not carry reads "not available for this date".
 *
 * The senior half of the retirement horizon (60 or over AND in band D or above)
 * is published once, by the single-incumbent senior roles block. This panel
 * quotes it only when that block carries the same date as this one, so a subset
 * never comes from a different date than its whole.
 */
import React from 'react';
import { BarRow } from './BarRow';
import { MetricTile, TileRow } from './MetricTile';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  ACCENT,
  Def,
  INK,
  basisLabel,
  categoryLabel,
  fmtNum,
  fmtOne,
  fmtPct,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

/** A served figure: a number, null (withheld), or undefined (not carried for this date). */
type Cell = number | null | undefined;

// =============================================================================
// ORDERING + LABELS
// =============================================================================

const AGE_ORDER = ['<30', '30-39', '40-49', '50-59', '60+'];
const SERVICE_ORDER = ['<1y', '1-2y', '2-5y', '5-10y', '10y+'];

const AGE_LABELS: Record<string, string> = {
  '<30': 'Under 30',
  '30-39': '30 to 39',
  '40-49': '40 to 49',
  '50-59': '50 to 59',
  '60+': '60 and over',
};
const SERVICE_LABELS: Record<string, string> = {
  '<1y': 'Under 1 year',
  '1-2y': '1 to 2 years',
  '2-5y': '2 to 5 years',
  '5-10y': '5 to 10 years',
  '10y+': '10 years or more',
};

const WITHHELD = 'withheld to protect small groups';
const NOT_AVAILABLE = 'not available for this date';
const WITHHELD_TIP =
  'Withheld to protect small groups: fewer than five people in this band, or a band that would let ' +
  'such a group be worked out from the total. Those people are still inside the total above.';

/** Server definition wins; the local text is only the floor. */
const tip = (def: DefLookup, k: string, fallback: string) => def(k) ?? fallback;
/** Same rule for MetricTile, whose `definition` prop beats the lookup. */
const localDef = (def: DefLookup, k: string, text: string) => (def(k) ? undefined : text);

const orderKeys = (available: string[], preferred: string[]) => [
  ...preferred.filter((k) => available.includes(k)),
  ...available.filter((k) => !preferred.includes(k)),
];

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const years = (n: number) => `${fmtOne(n)} yrs`;

/** A count identity withholds only below five, in the words of a sentence. */
const smallCount = (v: Cell) =>
  v === undefined ? NOT_AVAILABLE : v === null ? 'fewer than five' : fmtNum(v);

/** A served figure on a tile: null reads withheld, a missing key not available for this date. */
const TileFigure: React.FC<{ v: Cell; fmt: (n: number) => string }> = ({ v, fmt }) =>
  typeof v === 'number' ? (
    <>{fmt(v)}</>
  ) : (
    <span
      className="block text-[12px] font-medium leading-snug text-gray-400"
      title={v === null ? WITHHELD_TIP : undefined}
    >
      {capitalise(v === null ? WITHHELD : NOT_AVAILABLE)}
    </span>
  );

const NotAvailableNote: React.FC<{ what: string }> = ({ what }) => (
  <div className="rounded border border-gray-200 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-600">
    {what} is not available for this date.
  </div>
);

// =============================================================================
// DISTRIBUTION — one BarRow list, counts and shares, nulls held as withheld
// =============================================================================

const Distribution: React.FC<{
  heading: string;
  defKey: string;
  defFallback: string;
  def: DefLookup;
  bands: Record<string, number | null>;
  order: string[];
  labels: Record<string, string>;
  color: string;
  /** Share denominator: the whole population, so withheld bands still count. */
  denom: Cell;
}> = ({ heading, defKey, defFallback, def, bands, order, labels, color, denom }) => {
  const keys = orderKeys(Object.keys(bands), order);
  // Nulls are withheld, not zero — they are kept out of the scale entirely.
  const published = keys.map((k) => bands[k]).filter((v): v is number => typeof v === 'number');
  const max = published.length ? Math.max(...published) : 0;
  const hidden = keys.filter((k) => bands[k] === null).length;

  return (
    <div>
      <div className="mb-1 flex items-center text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        {heading}
        <Def def={def} k={defKey} text={tip(def, defKey, defFallback)} />
      </div>
      {keys.length === 0 ? (
        <div className="text-[11px] leading-snug text-gray-500">Not available for this date.</div>
      ) : null}
      {keys.map((k) => {
        const v = bands[k];
        const shown = typeof v === 'number' ? v : null;
        const share = shown === null || typeof denom !== 'number' || denom <= 0 ? null : (shown / denom) * 100;
        return (
          <BarRow
            key={k}
            label={labels[k] ?? k}
            value={shown}
            max={max}
            color={color}
            labelWidth={124}
            valueText={
              shown === null ? (
                <span className="text-[11px] font-normal text-gray-400" title={WITHHELD_TIP}>
                  Withheld
                </span>
              ) : (
                <span className="inline-block leading-tight">
                  <span className="block">{fmtNum(shown)}</span>
                  <span className="block text-[10px] font-normal text-gray-400">{fmtPct(share)}</span>
                </span>
              )
            }
          />
        );
      })}
      {hidden > 0 ? (
        <div className="mt-1 text-[11px] leading-snug text-gray-500">
          {hidden} band{hidden > 1 ? 's are' : ' is'} {WITHHELD} (fewer than five people, or a band
          that would let such a group be worked out from the total). Those people remain inside the
          population the shares are taken on, so the shares shown will not add to 100%.
        </div>
      ) : null}
    </div>
  );
};

// =============================================================================
// PANEL
// =============================================================================

export const AgeServicePanel: React.FC<Props> = ({ panels, basis, loading, def }) => {
  const block = readExtras(panels, 'age_service');

  const people: Cell = block?.people;
  const ageByCategory: Record<string, number> = block?.median_age_by_category ?? {};
  const svcByCategory: Record<string, number> = block?.median_service_by_category ?? {};
  const permAge: Cell = ageByCategory.Permanent;
  const ftcAge: Cell = ageByCategory.FTC;
  const ftcGap =
    typeof permAge === 'number' && typeof ftcAge === 'number' && permAge !== ftcAge
      ? `${Math.abs(permAge - ftcAge).toFixed(1)} years ${ftcAge < permAge ? 'younger' : 'older'} than the permanent cohort`
      : null;

  // The senior half of the 60+ figure belongs to the single-incumbent senior roles block.
  // Quoted only when both blocks state the SAME date — read off their own stamps, never a
  // frame date both could fall back to.
  const keyPerson = readExtras(panels, 'key_person');
  const ageDate = panels?.panels_provenance?.age_service?.computed_for ?? null;
  const keyPersonDate = panels?.panels_provenance?.key_person?.computed_for ?? null;
  const sameDate = keyPerson !== null && ageDate !== null && ageDate === keyPersonDate;
  // null with sixty_plus_withheld: fewer than five. null without it: not computed, so no clause.
  const seniors60: number | 'fewer than five' | null =
    !sameDate || !keyPerson
      ? null
      : typeof keyPerson.sixty_plus_seniors === 'number'
        ? keyPerson.sixty_plus_seniors
        : keyPerson.sixty_plus_withheld
          ? 'fewer than five'
          : null;

  const sixtyPlus: Cell = block?.sixty_plus;

  return (
    <PanelShell
      title="Age and length of service"
      defKey="age_service"
      def={def}
      window={
        <>
          {block ? (
            <>
              {panelDateLabel(panels, 'age_service')}
              {typeof people === 'number' ? <> — {fmtNum(people)} employees in post</> : null}.{' '}
            </>
          ) : null}
          Employees only (permanent and fixed-term); it does <b>not</b> follow the basis toggle
          {basis !== 'employees' ? (
            <> — you are viewing the {basisLabel(basis).toLowerCase()} basis above</>
          ) : null}{' '}
          or the period picker.
        </>
      }
      footnote={
        block ? (
          <>
            Service is measured from the person&apos;s{' '}
            <b>first position assignment recorded in the HR system</b>, so a rehire keeps their
            original start and a break in service is not netted off. Those records mostly begin in
            January 2019, when positions were first loaded, so for most people who joined earlier
            service counts from then, and long service is understated. Age and service are measured
            in years to the date shown. Shares are taken on all {fmtNum(people)} employees in post.
            Not recorded: birth date {smallCount(block.age_not_captured)}, first position assignment{' '}
            {smallCount(block.service_not_captured)}. A band {WITHHELD} is shown as withheld rather
            than dropped; those people still sit inside that total.
          </>
        ) : undefined
      }
    >
      {loading && !panels ? (
        <PanelSkeleton height={300} />
      ) : !panels ? (
        <PanelUnavailable what="The age and service profile" />
      ) : !block ? (
        <NotAvailableNote what="The age and service profile" />
      ) : (
        <div className="space-y-4">
          <TileRow min="180px">
            <MetricTile
              label="Median age"
              value={<TileFigure v={block.median_age} fmt={years} />}
              defKey="median_age"
              def={def}
              size="sm"
              definition={localDef(
                def,
                'median_age',
                'The middle employee by age — half the workforce is younger, half older. Less ' +
                  'distorted by a small number of long-serving older staff than a mean would be.',
              )}
              sub={typeof people === 'number' ? <>across {fmtNum(people)} employees</> : null}
            />
            <MetricTile
              label="Median service"
              value={<TileFigure v={block.median_service} fmt={years} />}
              defKey="median_service"
              def={def}
              size="sm"
              definition={localDef(
                def,
                'median_service',
                'The middle employee by length of service, measured from the first position ' +
                  'assignment recorded in the HR system. Breaks in service are not deducted.',
              )}
              sub={typeof block.median_service === 'number' ? 'since first recorded assignment' : null}
            />
            <MetricTile
              label="Median age · Permanent"
              value={<TileFigure v={permAge} fmt={years} />}
              defKey="median_age_permanent"
              def={def}
              size="sm"
              definition={localDef(
                def,
                'median_age_permanent',
                'Median age of employees whose most permanent position is a permanent contract.',
              )}
            />
            <MetricTile
              label={`Median age · ${categoryLabel('FTC')}`}
              value={<TileFigure v={ftcAge} fmt={years} />}
              defKey="median_age_ftc"
              def={def}
              size="sm"
              definition={localDef(
                def,
                'median_age_ftc',
                'Median age of employees whose most permanent position is a fixed-term contract. ' +
                  'The gap to the permanent cohort is the point: fixed-term hiring skews young.',
              )}
              sub={ftcGap}
            />
            <MetricTile
              label="Median service · Permanent"
              value={<TileFigure v={svcByCategory.Permanent} fmt={years} />}
              defKey="median_service_permanent"
              def={def}
              size="sm"
              definition={localDef(
                def,
                'median_service_permanent',
                'Median length of service of permanent employees, from their first position ' +
                  'assignment recorded in the HR system.',
              )}
            />
            <MetricTile
              label={`Median service · ${categoryLabel('FTC')}`}
              value={<TileFigure v={svcByCategory.FTC} fmt={years} />}
              defKey="median_service_ftc"
              def={def}
              size="sm"
              definition={localDef(
                def,
                'median_service_ftc',
                'Median length of service of fixed-term employees, from their first position ' +
                  'assignment recorded in the HR system.',
              )}
            />
          </TileRow>

          <div className="grid gap-x-6 gap-y-4 md:grid-cols-2">
            <Distribution
              heading="Age band"
              defKey="age_bands"
              defFallback={
                'Employees in post grouped by age on the date shown. The share is of all employees ' +
                'in post, so a withheld band leaves the shares short of 100%.'
              }
              def={def}
              bands={block.age_bands ?? {}}
              order={AGE_ORDER}
              labels={AGE_LABELS}
              color={INK}
              denom={people}
            />
            <Distribution
              heading="Length of service — from first recorded assignment"
              defKey="service_bands"
              defFallback={
                'Employees in post grouped by time since their first position assignment recorded ' +
                'in the HR system. Records mostly begin in January 2019, so the longest bands are ' +
                'understated.'
              }
              def={def}
              bands={block.service_bands ?? {}}
              order={SERVICE_ORDER}
              labels={SERVICE_LABELS}
              color={ACCENT}
              denom={people}
            />
          </div>

          {/* The 60+ age band restated as the pack's retirement horizon. A copy that does not
              carry the figure shows no line: the band above already carries those people. */}
          {sixtyPlus !== undefined ? (
            <div className="flex items-center text-[12px] text-gray-600">
              <span>
                {typeof sixtyPlus === 'number' ? (
                  <>
                    <b>{fmtNum(sixtyPlus)}</b> {sixtyPlus === 1 ? 'employee is' : 'employees are'} aged
                    60 or over
                    {seniors60 !== null ? (
                      <>
                        , of whom{' '}
                        <b>{typeof seniors60 === 'number' ? fmtNum(seniors60) : seniors60}</b>{' '}
                        {seniors60 === 1 ? 'holds' : 'hold'} a role in band D or above
                      </>
                    ) : null}
                    .
                  </>
                ) : (
                  <>The number of employees aged 60 or over is {WITHHELD}.</>
                )}
              </span>
              <Def
                def={def}
                k="retirement_horizon"
                text={tip(
                  def,
                  'retirement_horizon',
                  'Employees in post aged 60 or over on the date shown. When the single-incumbent ' +
                    'senior roles panel is dated the same day, also how many of them hold a role in ' +
                    'band D or above.',
                )}
              />
            </div>
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
